import React, { useState } from 'react';
import {
  CalendarMonthOutlined,
  CheckOutlined,
  ContentCopyOutlined,
  FingerprintOutlined,
  InfoOutlined,
  ShieldOutlined,
  TagOutlined,
} from '@mui/icons-material';
import cx from 'classnames';

import { fallbackCopyTextToClipboard } from 'js/lib/MiscUtils';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import screenStyles from 'new/modules/settings/capsules/screens/CapsuleDetailScreen/CapsuleDetailScreen.module.scss';
import styles from './CapsuleDetailsSection.module.scss';

interface DetailField {
  key: string;
  label: string;
  value: string;
  icon: React.ReactNode;
  copyLabel?: string;
}

interface CapsuleDetailsSectionProps {
  capsuleId?: string;
  uid?: string | null;
  orgId?: string;
  createdAt?: string;
  description?: string;
}

export function CapsuleDetailsSection({
  capsuleId,
  uid,
  orgId,
  createdAt,
  description,
}: CapsuleDetailsSectionProps) {
  const { showSuccess } = useSnackbar();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const fields: DetailField[] = [
    capsuleId && {
      key: 'id',
      label: 'Capsule ID',
      value: capsuleId,
      icon: <TagOutlined className={styles.labelIcon} />,
      copyLabel: 'Capsule ID',
    },
    uid && {
      key: 'uid',
      label: 'Dataplane UID',
      value: uid,
      icon: <FingerprintOutlined className={styles.labelIcon} />,
      copyLabel: 'Dataplane UID',
    },
    orgId && {
      key: 'orgId',
      label: 'Org ID',
      value: orgId,
      icon: <ShieldOutlined className={styles.labelIcon} />,
      copyLabel: 'Org ID',
    },
    createdAt && {
      key: 'createdAt',
      label: 'Created At',
      value: createdAt,
      icon: <CalendarMonthOutlined className={styles.labelIcon} />,
    },
  ].filter(Boolean) as DetailField[];

  const handleCopy = async (field: DetailField) => {
    try {
      await fallbackCopyTextToClipboard(field.value);
      setCopiedKey(field.key);
      showSuccess('Copied to clipboard!', 2000);
      setTimeout(() => {
        setCopiedKey((current) => (current === field.key ? null : current));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy value', error);
    }
  };

  if (fields.length === 0 && !description) return null;

  return (
    <>
      {fields.length > 0 && (
        <div className={styles.card}>
          <div className={styles.header}>
            <span className={styles.headerIcon}>
              <InfoOutlined fontSize="small" />
            </span>
            <h2 className={styles.headerTitle}>Capsule Details</h2>
          </div>

          <div className={styles.grid}>
            {fields.map((field) => {
              const isCopied = copiedKey === field.key;
              const copyable = Boolean(field.copyLabel);
              return (
                <div key={field.key} className={styles.cell}>
                  <div className={styles.label}>
                    {field.icon}
                    {field.label}
                  </div>
                  <div className={styles.valueRow}>
                    {copyable ? (
                      <button
                        type="button"
                        className={styles.valueTag}
                        onClick={() => handleCopy(field)}
                        aria-label={`Copy ${field.copyLabel}`}
                      >
                        {field.value}
                      </button>
                    ) : (
                      <span className={styles.valueText}>{field.value}</span>
                    )}
                    {copyable && (
                      <button
                        type="button"
                        className={cx(styles.copyButton, {
                          [styles.copyButtonSuccess]: isCopied,
                        })}
                        onClick={() => handleCopy(field)}
                        aria-label={`Copy ${field.copyLabel}`}
                      >
                        {isCopied ? (
                          <CheckOutlined fontSize="small" />
                        ) : (
                          <ContentCopyOutlined fontSize="small" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {description ? (
        <div className={styles.description}>
          <div className={screenStyles.detailLabel}>Description</div>
          <p className={screenStyles.description}>{description}</p>
        </div>
      ) : null}
    </>
  );
}
